import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LOGO_URL } from '@/data/mockData';
import { useAuth } from '@/context/AuthContext';
import { useSalesTracker } from '@/hooks/useSalesTracker';
import { API, xhrJson } from '@/lib/api';
import SalesWizard from '@/components/shared/SalesWizard';
import { Plus, Play, ArrowLeft, Mic, Eye, EyeOff } from 'lucide-react';
import { useVoiceRecorder } from '@/hooks/useVoiceRecorder';
import RecordingBar from '@/components/shared/RecordingBar';
import ShowcaseStack from '@/components/landing/ShowcaseStack';

function RocketIcon({ className }) {
  return (
    <svg viewBox="0 0 600 600" className={className} xmlns="http://www.w3.org/2000/svg">
      <g transform="translate(0,600) scale(0.1,-0.1)" fill="currentColor" stroke="none">
        <path d="M2941 4945 c-53 -48 -119 -149 -202 -310 -91 -176 -163 -364 -249 -645 -156 -513 -274 -797 -481 -1152 -144 -247 -174 -325 -233 -603 -62 -295 -129 -830 -112 -898 7 -31 34 -41 72 -27 16 6 185 169 384 371 195 198 361 360 367 359 7 0 28 -31 47 -68 19 -38 47 -83 61 -100 l27 -32 372 0 c314 0 375 2 393 15 12 8 42 53 68 100 25 47 51 85 58 85 7 1 167 -156 356 -347 189 -191 355 -356 370 -365 39 -27 79 -23 93 8 21 46 -37 548 -97 843 -63 312 -106 430 -238 652 -179 302 -313 608 -437 999 -169 531 -181 564 -297 793 -69 136 -165 288 -204 324 -44 40 -72 39 -118 -2z"/>
        <path d="M2763 1638 c15 -188 133 -458 212 -488 37 -14 64 3 112 69 70 95 132 274 148 424 l7 67 -242 0 -243 0 6 -72z"/>
      </g>
    </svg>
  );
}

const AI_FALLBACK_REPLY =
  "We're having a small issue answering right now. " +
  "Tell us what kind of video you have in mind and our team will guide you — " +
  "or check out the credit packages just below.";

const PENDING_CHECKOUT_KEY = 'ruido_pending_checkout_pkg';
const SALES_VISITOR_KEY = 'ruido_sales_visitor_v1';
const SALES_CONV_KEY = 'ruido_sales_conv_v1';

function getVisitorToken() {
  try {
    let t = localStorage.getItem(SALES_VISITOR_KEY);
    if (!t) {
      t = (typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `vis-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
      localStorage.setItem(SALES_VISITOR_KEY, t);
    }
    return t;
  } catch { return `vis-${Date.now()}`; }
}

const EXAMPLE_VIDEOS = [
  { title: 'Restaurant Promo', label: 'Short promo example', thumbnail: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=400&h=225&fit=crop&q=80' },
  { title: 'Brand Story', label: 'Standard promo example', thumbnail: 'https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?w=400&h=225&fit=crop&q=80' },
  { title: 'Product Launch', label: 'Full video example', thumbnail: 'https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=400&h=225&fit=crop&q=80' },
];

const CREDIT_EXPLANATION =
  "Credits represent real creative work: idea, script, design, animation and editing done by our team.\n\n" +
  "Each credit covers about 3 seconds of finished video. You buy a package once and use it whenever you want.";

const AUTO_WEB_INTRO = [
  {
    role: 'assistant',
    text:
      "This isn't automated content. Every video is built by a real team: idea, script, design, animation and editing.\n\n" +
      "We use AI as a tool, but every piece is conceived, crafted and reviewed by people.",
  },
  {
    role: 'assistant',
    text:
      "That's why the system runs on credits: every second of video means real work.\n\n" +
      "Pick the rough length below and we'll turn it into a professional piece ready for your brand.",
  },
];

const AUTO_WEB_OUTRO = [
  { role: 'assistant', text: "Have questions or want help picking? Type below and we'll reply." },
];

export default function EntryScreen({ initialMode = null }) {
  const navigate = useNavigate();
  const isPublicDefault = !initialMode;
  const [chatInput, setChatInput] = useState('');
  const [chatMessages, setChatMessages] = useState(isPublicDefault ? AUTO_WEB_INTRO : []);
  const [chatActive, setChatActive] = useState(isPublicDefault);
  const [showAuth, setShowAuth] = useState(false);
  const { isLoggedIn, profile, signUp, signIn, signInWithGoogle, signOut, session, resendConfirmation } = useAuth();
  const accessTokenGetter = useRef(null);
  accessTokenGetter.current = () => session?.access_token || null;
  const { trackMessage, linkSignup } = useSalesTracker(() => accessTokenGetter.current?.());
  const [usedChips, setUsedChips] = useState(isPublicDefault ? { examples: true, credits: true } : {});
  const cameFromAccount = isLoggedIn && initialMode === 'credits';
  const [authMode, setAuthMode] = useState(null); // null | 'signin' | 'signup'
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState('');
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);
  const aiConvIdRef = useRef(null);
  const [aiThinking, setAiThinking] = useState(false);
  // True after the visitor types `/manager` (with or without secret). Forces
  // the auth submit handler to verify role=manager and show "Acceso no
  // autorizado" instead of falling through to /cliente.
  const [managerLoginMode, setManagerLoginMode] = useState(false);

  // Restore persisted AI sales conversation id (so multi-turn context survives reloads)
  useEffect(() => {
    try {
      const stored = localStorage.getItem(SALES_CONV_KEY);
      if (stored) aiConvIdRef.current = stored;
    } catch { /* noop */ }
  }, []);

  const canSend = chatInput.trim().length > 0;
  const hasMessages = chatMessages.length > 0;
  const [scrolledPast, setScrolledPast] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  // Mobile UX: when the user scrolls DOWN past a tiny threshold we hide the
  // top chrome (logo/header + onboarding chips) so the hero video gets full
  // visual space. When they scroll UP again, everything fades back in. The
  // bottom chat input is never affected — it always stays mounted, fully
  // interactive, anchored above the safe-area inset. (Mobile only via CSS.)
  const [hideChrome, setHideChrome] = useState(false);
  const lastScrollYRef = useRef(0);
  const scrollAreaRef = useRef(null);

  // Hero expansion policy:
  //  - Public default: the expanded hero is rendered UNCONDITIONALLY in the
  //    natural flow. It scrolls away on its own, pushed by the Showcase
  //    section below it. A separate zero-height sticky overlay fades in
  //    progressively from above as the user scrolls past the hero, so the
  //    compact "Ruido.ai" badge never "jumps" into view — it blends in
  //    smoothly while the video continues upward without reflow.
  //  - Other modes (auth pane, credits flow): keep the legacy sticky-swap
  //    behavior which is tied to chat state, not pure scroll.
  const collapseThreshold = isPublicDefault ? 240 : 30;
  const heroExpanded = isPublicDefault
    ? true
    : !scrolledPast && (!chatActive || showAuth);

  useEffect(() => {
    const el = scrollAreaRef.current;
    if (!el) return;
    // Start fading the compact header in at 120px, full at 240px.
    const FADE_START = 120;
    const FADE_END = collapseThreshold; // 240px public / 30px otherwise
    const onScroll = () => {
      const y = el.scrollTop;
      setScrolledPast(y > collapseThreshold);
      if (FADE_END > FADE_START) {
        const p = Math.max(0, Math.min(1, (y - FADE_START) / (FADE_END - FADE_START)));
        setScrollProgress((prev) => (Math.abs(prev - p) > 0.01 ? p : prev));
      }
      // Direction-aware chrome visibility. Tiny dead zone to ignore jitter.
      const delta = y - lastScrollYRef.current;
      if (Math.abs(delta) > 6) {
        if (delta > 0 && y > 60) {
          // Scrolling DOWN past a small initial threshold → hide chrome.
          setHideChrome((prev) => (prev ? prev : true));
        } else if (delta < 0) {
          // Scrolling UP → bring chrome back.
          setHideChrome((prev) => (prev ? false : prev));
        }
        lastScrollYRef.current = y;
      }
      // At very top, always show chrome (avoid stuck-hidden state if the user
      // bounce-scrolls or refreshes mid-scroll).
      if (y <= 4) {
        setHideChrome((prev) => (prev ? false : prev));
        lastScrollYRef.current = 0;
      }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [collapseThreshold]);

  // Phase 6.5 — Load real video sizes + packages from backend and append to chat
  const dynamicLoadedRef = useRef(false);
  useEffect(() => {
    if (!isPublicDefault || dynamicLoadedRef.current) return;
    if (initialMode === 'credits') return; // /credits override handles its own load
    dynamicLoadedRef.current = true;
    (async () => {
      const [sizesRes, pkgsRes] = await Promise.all([
        xhrJson('GET', `${API}/api/credits/video-sizes`, null, {}),
        xhrJson('GET', `${API}/api/credits/packages`, null, {}),
      ]);
      const sizes = (sizesRes.ok && sizesRes.data?.video_sizes) || [];
      const pkgs = (pkgsRes.ok && pkgsRes.data?.packages) || [];
      const stripeEnabled = !!pkgsRes.data?.stripe_enabled;

      // Best per-credit price across packages → use to estimate per-size cost.
      // Tolerate legacy `price_eur` field if migration 010 hasn't run yet.
      const priceOf = (p) => (p.price_usd != null ? p.price_usd : p.price_eur);
      const perCreditPrices = pkgs
        .filter((p) => priceOf(p) && p.credits)
        .map((p) => priceOf(p) / p.credits);
      const cheapestUnit = perCreditPrices.length ? Math.min(...perCreditPrices) : null;

      const sizeCards = sizes.map((s) => {
        const range = s.min_seconds && s.max_seconds ? `${s.min_seconds}–${s.max_seconds} seg` : '';
        const price = cheapestUnit && s.credits_required
          ? Math.round(cheapestUnit * s.credits_required)
          : null;
        return {
          id: s.id,
          name: s.label || s.id,
          duration: range,
          credits: s.credits_required,
          description: s.description || '',
          approxPrice: price,
        };
      });
      const pkgItems = pkgs.map((p) => {
        const usd = priceOf(p);
        return {
          id: p.id,
          name: p.name,
          qty: `${p.credits} credits`,
          price: usd != null ? `$${usd} USD` : '',
          unit: usd != null && p.credits ? `$${(usd / p.credits).toFixed(2)}/cr.` : '',
          description: p.description || '',
          featured: !!p.highlight,
          credits: p.credits,
          amount_usd: usd,
        };
      });

      const additions = [];
      if (sizeCards.length) {
        additions.push({
          role: 'assistant',
          type: 'size-section',
          title: 'Choose the video type',
          subtitle:
            "Cost is based on production time.\n\n" +
            "Each credit covers about 3 seconds of finished video, including ideation, design, animation and editing by our team.\n\n" +
            "Pick a length range and you'll see the credits required.",
          cards: sizeCards,
        });
      }
      if (pkgItems.length) {
        additions.push({
          role: 'assistant',
          type: 'packages-section',
          title: 'Buy credits',
          subtitle: stripeEnabled
            ? 'Pick a package and complete the payment.'
            : 'Pre-order now; a manager will grant the credits.',
          items: pkgItems,
          stripeEnabled,
        });
      }
      additions.push(...AUTO_WEB_OUTRO);
      setChatMessages((prev) => [...prev, ...additions]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dynamicPlaceholder = isLoggedIn
    ? 'Ask about your project, credits, or delivery...'
    : chatActive && chatMessages.some(m => m.type === 'plans' || m.type === 'packages')
      ? 'Ask anything about credits, delivery, or video styles...'
      : chatActive && chatMessages.some(m => m.type === 'examples')
        ? 'Ask about style, pricing, or delivery times...'
        : 'Ask anything about pricing or video options...';

  // Tracks whether the visitor has actively interacted (sent a message, used
  // the wizard, etc.). The chat auto-scroll-to-bottom only fires after that —
  // before that, dynamic seed messages (AUTO_WEB_INTRO, packages, outro) would
  // otherwise scroll the visitor past the showcase and hero.
  const userInteractedRef = useRef(false);
  useEffect(() => {
    if (!userInteractedRef.current) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages.length]);

  // Auto-trigger credits mode when navigating from /credits (once only)
  const creditsTriggeredRef = useRef(false);
  useEffect(() => {
    if (initialMode === 'credits' && !creditsTriggeredRef.current) {
      creditsTriggeredRef.current = true;
      setChatActive(true);
      setUsedChips(prev => ({ ...prev, credits: true }));
      setChatMessages([
        { role: 'assistant', text: "Here's how our credit system works:" },
        { role: 'assistant', text: CREDIT_EXPLANATION },
      ]);
      (async () => {
        const [sizesRes, pkgsRes] = await Promise.all([
          xhrJson('GET', `${API}/api/credits/video-sizes`, null, {}),
          xhrJson('GET', `${API}/api/credits/packages`, null, {}),
        ]);
        const sizes = (sizesRes.ok && sizesRes.data?.video_sizes) || [];
        const pkgs = (pkgsRes.ok && pkgsRes.data?.packages) || [];
        const stripeEnabled = !!pkgsRes.data?.stripe_enabled;
        const priceOf = (p) => (p.price_usd != null ? p.price_usd : p.price_eur);
        const perCreditPrices = pkgs
          .filter((p) => priceOf(p) && p.credits)
          .map((p) => priceOf(p) / p.credits);
        const cheapestUnit = perCreditPrices.length ? Math.min(...perCreditPrices) : null;
        const additions = [];
        if (sizes.length) {
          additions.push({
            role: 'assistant', type: 'size-section',
            title: 'Choose the video type',
            subtitle:
              "Cost is based on production time.\n\n" +
              "Each credit covers about 3 seconds of finished video, including ideation, design, animation and editing by our team.\n\n" +
              "Pick a length range and you'll see the credits required.",
            cards: sizes.map((s) => ({
              id: s.id, name: s.label || s.id,
              duration: s.min_seconds && s.max_seconds ? `${s.min_seconds}–${s.max_seconds} sec` : '',
              credits: s.credits_required, description: s.description || '',
              approxPrice: cheapestUnit && s.credits_required
                ? Math.round(cheapestUnit * s.credits_required)
                : null,
            })),
          });
        }
        if (pkgs.length) {
          additions.push({
            role: 'assistant', type: 'packages-section',
            title: 'Buy credits',
            subtitle: stripeEnabled
              ? 'Pick a package and complete the payment.'
              : 'Pre-order now; a manager will grant the credits.',
            stripeEnabled,
            items: pkgs.map((p) => {
              const usd = priceOf(p);
              return {
                id: p.id, name: p.name, qty: `${p.credits} credits`,
                price: usd != null ? `$${usd} USD` : '',
                unit: usd != null && p.credits ? `$${(usd / p.credits).toFixed(2)}/cr.` : '',
                description: p.description || '',
                featured: !!p.highlight, credits: p.credits, amount_usd: usd,
              };
            }),
          });
        }
        additions.push({ role: 'assistant', text: "Each option reflects production time and quality. Want help choosing?" });
        setChatMessages((prev) => [...prev, ...additions]);
      })();
    }
  }, []);

  const handleSend = async () => {
    if (!canSend || aiThinking) return;
    const userText = chatInput.trim();

    // Hidden manager command — never echoed in chat or tracked.
    // Forms supported:
    //   "/manager"            → opens login pane (no automatic auth)
    //   "/manager <secret>"   → validates secret then opens login pane
    const mgrFull = userText.match(/^\/manager(?:\s+(.+))?$/i);
    if (mgrFull) {
      setChatInput('');
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
      const secret = (mgrFull[1] || '').trim();
      if (secret) {
        handleManagerSecret(secret);
      } else {
        openManagerLogin();
      }
      return;
    }

    setChatMessages(prev => [...prev, { role: 'user', text: userText }]);
    userInteractedRef.current = true;
    setChatInput('');
    setChatActive(true);
    setShowAuth(false);
    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    // Track visitor message → sales backend (keeps the manager inbox in sync)
    const lower = userText.toLowerCase();
    let intent;
    if (/precio|price|cost|cuesta|cuanto|cu[aá]nto/.test(lower)) intent = 'pricing';
    else if (/credit|crédito|creditos|créditos|paquete|package/.test(lower)) intent = 'credits';
    else if (/example|ejemplo/.test(lower)) intent = 'examples';
    trackMessage({ sender_type: 'visitor', content: userText, intent });

    // Call OpenAI sales bot
    setAiThinking(true);
    try {
      const visitorToken = getVisitorToken();
      const headers = session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {};
      const { ok, data } = await xhrJson(
        'POST',
        `${API}/api/sales/ai-chat`,
        {
          visitor_token: visitorToken,
          conversation_id: aiConvIdRef.current,
          message: userText,
        },
        headers,
        30000,
      );
      if (ok && data?.conversation_id) {
        aiConvIdRef.current = data.conversation_id;
        try { localStorage.setItem(SALES_CONV_KEY, data.conversation_id); } catch { /* noop */ }
      }
      const reply = (ok && data?.reply) ? data.reply : AI_FALLBACK_REPLY;
      const meta = (ok && data?.metadata) || {};
      setChatMessages(prev => [...prev, { role: 'assistant', text: reply, metadata: meta }]);

      // Hybrid close: if AI suggests a specific package, surface a quick CTA.
      if (ok && meta.package_id) {
        setChatMessages(prev => [
          ...prev,
          {
            role: 'assistant',
            type: 'actions',
            actions: [
              { label: `Buy ${meta.package_id}`, action: `buy:${meta.package_id}` },
              { label: 'See all packages', action: 'credits' },
            ],
          },
        ]);
      }
    } catch (err) {
      setChatMessages(prev => [...prev, { role: 'assistant', text: AI_FALLBACK_REPLY }]);
    } finally {
      setAiThinking(false);
    }
  };

  // Opens the inline auth pane in sign-in mode and flags the session as a
  // manager-login attempt. After auth, the submit handler will verify
  // profile.role === 'manager'|'admin' and reject otherwise.
  const openManagerLogin = () => {
    setManagerLoginMode(true);
    setChatActive(true);
    // If already logged in as manager → straight in.
    if (isLoggedIn && (profile?.role === 'manager' || profile?.role === 'admin')) {
      navigate('/gestor');
      return;
    }
    if (isLoggedIn) {
      // Logged in but not manager — show explicit denial.
      setManagerLoginMode(false);
      setChatMessages(prev => [
        ...prev,
        { role: 'assistant', text: 'Unauthorized access.' },
      ]);
      return;
    }
    // Not logged in — show login pane.
    setShowAuth(true);
    setAuthMode('signin');
    setAuthError('');
    setChatMessages(prev => [
      ...prev,
      { role: 'assistant', text: 'Sign in with your manager account to continue.' },
    ]);
    // Adding a chat message triggers an auto-scroll-to-bottom effect that
    // collapses the hero (scrolledPast=true) — which hides the auth form.
    // After the next paint, override and force the hero back open.
    setTimeout(() => {
      try { scrollAreaRef.current?.scrollTo(0, 0); } catch { /* noop */ }
      setScrolledPast(false);
    }, 120);
  };

  const handleManagerSecret = async (secret) => {
    const { ok, data } = await xhrJson(
      'POST',
      `${API}/api/manager/auth/secret`,
      { secret },
    );
    if (!ok || !data?.ok) {
      setChatActive(true);
      setChatMessages(prev => [
        ...prev,
        { role: 'assistant', text: 'Invalid access.' },
      ]);
      return;
    }
    // Secret OK → open login flow (will route based on role after submit).
    openManagerLogin();
  };

  // Phase 6 — Sales wizard handlers
  const handleWizardAnswer = (step, value, label, stepNumber) => {
    setChatActive(true);
    userInteractedRef.current = true;
    setChatMessages(prev => [
      ...prev,
      { role: 'user', text: label },
    ]);
    trackMessage({
      sender_type: 'visitor',
      content: label,
      intent: 'guided_sales',
      message_type: 'text',
      metadata: { wizard_step: stepNumber, key: step.id, value },
    });
    // Friendly assistant ack, with hints based on the question
    const ack = (() => {
      if (step.id === 'business') return "Perfect, noted. That helps us tune the tone.";
      if (step.id === 'goal')     return "Great — that goal guides the script and pacing of the video.";
      if (step.id === 'size')     return "Noted. When you start the project we'll confirm the exact credits.";
      if (step.id === 'urgency')  return "Got it. We work with a 24h universal SLA by default and speed up if it's urgent.";
      if (step.id === 'volume')   return "Done. For volume the big package works best — you save per credit.";
      return 'Noted.';
    })();
    setTimeout(() => {
      setChatMessages(prev => [...prev, { role: 'assistant', text: ack }]);
      trackMessage({ sender_type: 'assistant', content: ack, intent: 'guided_sales' });
    }, 500);
  };

  const handleWizardComplete = (answers) => {
    setShowAuth(true);
    setAuthMode('signup');
    setChatActive(true);
    const summary = `Brief recogido (${Object.entries(answers).map(([k, v]) => `${k}:${v}`).join(' · ')}).`;
    setChatMessages(prev => [
      ...prev,
      { role: 'assistant', text: 'Crea tu cuenta y guardamos esto como tu primer brief.' },
    ]);
    trackMessage({
      sender_type: 'assistant',
      content: 'Wizard completed → opened signup',
      intent: 'guided_sales_complete',
      message_type: 'system',
      metadata: { wizard_answers: answers, summary },
    });
  };

  const handleWizardSkip = () => {
    trackMessage({
      sender_type: 'visitor',
      content: 'wizard_skipped',
      intent: 'guided_sales_skip',
      message_type: 'system',
      metadata: { wizard_skipped: true },
    });
  };

  const handleCredits = async () => {
    if (usedChips.credits) return;
    setChatActive(true);
    setShowAuth(false);
    setUsedChips(prev => ({ ...prev, credits: true }));
    setChatMessages(prev => [
      ...prev,
      { role: 'assistant', text: "Here's how our credit system works:" },
      { role: 'assistant', text: CREDIT_EXPLANATION },
    ]);
    const [sizesRes, pkgsRes] = await Promise.all([
      xhrJson('GET', `${API}/api/credits/video-sizes`, null, {}),
      xhrJson('GET', `${API}/api/credits/packages`, null, {}),
    ]);
    const sizes = (sizesRes.ok && sizesRes.data?.video_sizes) || [];
    const pkgs = (pkgsRes.ok && pkgsRes.data?.packages) || [];
    const stripeEnabled = !!pkgsRes.data?.stripe_enabled;
    const priceOf = (p) => (p.price_usd != null ? p.price_usd : p.price_eur);
    const perCreditPrices = pkgs
      .filter((p) => priceOf(p) && p.credits)
      .map((p) => priceOf(p) / p.credits);
    const cheapestUnit = perCreditPrices.length ? Math.min(...perCreditPrices) : null;
    const additions = [];
    if (sizes.length) {
      additions.push({
        role: 'assistant', type: 'size-section',
        title: 'Choose the video type',
        subtitle:
          "Cost is based on production time.\n\n" +
          "Each credit covers about 3 seconds of finished video, including ideation, design, animation and editing by our team.\n\n" +
          "Pick a length range and you'll see the credits required.",
        cards: sizes.map((s) => ({
          id: s.id, name: s.label || s.id,
          duration: s.min_seconds && s.max_seconds ? `${s.min_seconds}–${s.max_seconds} sec` : '',
          credits: s.credits_required, description: s.description || '',
          approxPrice: cheapestUnit && s.credits_required
            ? Math.round(cheapestUnit * s.credits_required)
            : null,
        })),
      });
    }
    if (pkgs.length) {
      additions.push({
        role: 'assistant', type: 'packages-section',
        title: 'Buy credits',
        subtitle: stripeEnabled
          ? 'Pick a package and complete the payment.'
          : 'Pre-order now; a manager will grant the credits.',
        stripeEnabled,
        items: pkgs.map((p) => {
          const usd = priceOf(p);
          return {
            id: p.id, name: p.name, qty: `${p.credits} credits`,
            price: usd != null ? `$${usd} USD` : '',
            unit: usd != null && p.credits ? `$${(usd / p.credits).toFixed(2)}/cr.` : '',
            description: p.description || '',
            featured: !!p.highlight, credits: p.credits, amount_usd: usd,
          };
        }),
      });
    }
    additions.push({ role: 'assistant', text: "Each option reflects production time and quality. Want help choosing?" });
    setChatMessages(prev => [...prev, ...additions]);
    trackMessage({
      sender_type: 'visitor',
      content: 'Tapped: View credits',
      message_type: 'action',
      intent: 'credits',
    });
  };

  const handleExamples = () => {
    setChatActive(true);
    setShowAuth(false);
    setUsedChips(prev => ({ ...prev, examples: true }));
    setChatMessages(prev => [
      ...prev,
      { role: 'assistant', text: "Here are a few examples of the kind of videos we create." },
      { role: 'assistant', type: 'examples', videos: EXAMPLE_VIDEOS },
      { role: 'assistant', text: "Want something like this for your business?" },
      { role: 'assistant', type: 'actions', actions: [
        { label: 'Buy credits', action: 'credits' },
        { label: 'Ask a question', action: 'focus' },
      ]},
    ]);
    trackMessage({
      sender_type: 'visitor',
      content: 'Tapped: See examples',
      message_type: 'action',
      intent: 'examples',
    });
  };

  const handleChatAction = (action) => {
    if (action === 'credits') handleCredits();
    else if (action === 'focus') textareaRef.current?.focus();
    else if (action === 'settings') handleSettings();
    else if (typeof action === 'string' && action.startsWith('buy:')) {
      const pkgId = action.slice(4);
      handleGetCredits({ id: pkgId });
    } else if (action === 'setting-name') {
      setChatMessages(prev => [...prev,
        { role: 'assistant', text: "To change your display name, type your new name below and send it." },
      ]);
      textareaRef.current?.focus();
    } else if (action === 'setting-profile') {
      setChatMessages(prev => [...prev,
        { role: 'assistant', text: "Your profile settings include your display name, email, and avatar. What would you like to update?" },
      ]);
      textareaRef.current?.focus();
    } else if (action === 'setting-privacy') {
      setChatMessages(prev => [...prev,
        { role: 'assistant', text: "Your data is private by default. Project files are only visible to you and your assigned production team. Need to change anything?" },
      ]);
    } else if (action === 'setting-help') {
      setChatMessages(prev => [...prev,
        { role: 'assistant', text: "Ask anything about your videos, credits, or delivery. I'm here to help." },
      ]);
      textareaRef.current?.focus();
    } else if (action === 'setting-logout') {
      signOut();
      setChatMessages(prev => [...prev,
        { role: 'assistant', text: "You've been logged out. See you next time!" },
      ]);
    }
  };

  const handleSettings = () => {
    setChatActive(true);
    setShowAuth(false);
    setChatMessages(prev => [...prev,
      { role: 'assistant', text: "What would you like to do?" },
      { role: 'assistant', type: 'actions', actions: [
        { label: 'Change name', action: 'setting-name' },
        { label: 'Profile settings', action: 'setting-profile' },
        { label: 'Privacy', action: 'setting-privacy' },
        { label: 'Help', action: 'setting-help' },
        { label: 'Log out', action: 'setting-logout' },
      ]},
    ]);
  };

  const handleGetCredits = async (pkg) => {
    const pkgId = typeof pkg === 'string' ? pkg : pkg?.id;
    const pkgLabel = typeof pkg === 'string' ? pkg : (pkg?.qty || pkg?.name || '');
    if (!isLoggedIn) {
      // Save intent so we resume after signup
      try { localStorage.setItem(PENDING_CHECKOUT_KEY, pkgId || ''); } catch { /* noop */ }
      setChatMessages(prev => [...prev,
        { role: 'assistant', text: `Anotado${pkgLabel ? ` — ${pkgLabel}` : ''}. Crea tu cuenta para terminar la compra.` },
      ]);
      setShowAuth(true);
      setAuthMode('signup');
      trackMessage({
        sender_type: 'visitor', content: `buy_intent:${pkgId || pkgLabel}`,
        intent: 'buy', message_type: 'system',
        metadata: { package_id: pkgId },
      });
      return;
    }
    if (!pkgId) {
      setChatMessages(prev => [...prev,
        { role: 'assistant', text: 'Select a specific package to continue.' },
      ]);
      return;
    }
    // Call placeholder checkout (writes a `purchases` row).
    const { ok, status, data } = await xhrJson(
      'POST', `${API}/api/credits/checkout`,
      { package_id: pkgId },
      { Authorization: `Bearer ${session?.access_token || ''}` },
    );
    if (!ok) {
      const msg = (data && data.detail) || `Error ${status || ''}`;
      setChatMessages(prev => [...prev, { role: 'assistant', text: `Couldn't register the purchase: ${msg}` }]);
      return;
    }
    setChatMessages(prev => [...prev, {
      role: 'assistant',
      text: data?.message || (data?.stripe_enabled
        ? 'Opening secure payment…'
        : `Pre-order registered${pkgLabel ? ` for ${pkgLabel}` : ''}. A manager will convert it to credits shortly.`),
    }]);
    trackMessage({
      sender_type: 'visitor', content: `checkout:${pkgId}`,
      intent: 'checkout', message_type: 'system',
      metadata: { package_id: pkgId, stripe_enabled: !!data?.stripe_enabled },
    });
  };

  // Resume pending checkout after signup
  useEffect(() => {
    if (!isLoggedIn || !session?.access_token) return;
    let pending = null;
    try { pending = localStorage.getItem(PENDING_CHECKOUT_KEY); } catch { pending = null; }
    if (!pending) return;
    try { localStorage.removeItem(PENDING_CHECKOUT_KEY); } catch { /* noop */ }
    handleGetCredits({ id: pending });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, session?.access_token]);

  const handleLogoClick = () => {
    setShowAuth(true);
    scrollAreaRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFocus = () => {
    setChatActive(true);
    setShowAuth(false);
  };

  const voice = useVoiceRecorder({
    language: 'en',
    prompt: 'Conversation about video production with AI, credits, pricing and Ruido.ai projects.',
    maxSeconds: 300,
    onTranscribed: (text) => {
      setChatActive(true);
      setShowAuth(false);
      setChatInput(prev => (prev ? `${prev} ${text}` : text));
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (el) {
          el.focus();
          el.style.height = 'auto';
          el.style.height = Math.min(el.scrollHeight, 140) + 'px';
        }
      });
    },
  });

  return (
    <div className="h-[100dvh] bg-[#f0efed] flex flex-col overflow-hidden relative" data-testid="entry-screen">
      {/* Manager access is hidden — use `/manager <secret>` in chat or a magic /m/<token> link. */}
      {/* Scrollable area — everything scrolls, header is sticky on top */}
      <div className="flex-1 min-h-0 overflow-y-auto" data-testid="scroll-area" ref={scrollAreaRef}>
        {/* Hero / sticky header.
            - Public default + expanded: rendered in normal flow (no z-index,
              no glass backdrop) so the headline and auth links scroll away
              naturally as the user drags upward. The showcase card sits in
              its own section BELOW and pushes the hero out of the viewport.
            - Once the user has scrolled past the hero (240px threshold),
              we pin a compact `Ruido.ai` badge at top-0 with the glass
              backdrop for branded presence while browsing the showcase.
            - Other modes (auth, credits, chat) keep the legacy behavior
              — sticky + glass from the start.
        */}
        {(() => {
          // Non-public modes (auth, credits, chat) keep the legacy single-container
          // sticky-swap behavior. Only the public landing uses the smooth
          // scroll-driven fade-in architecture (two parallel elements).
          const legacySwap = !isPublicDefault;
          const compactMode = legacySwap ? !heroExpanded : false;
          return (
            <>
            {/* Compact sticky overlay — PUBLIC ONLY. Zero-height sticky trick:
                the outer div is sticky top-0 with height: 0 so it never takes
                up any space in flow (no layout jump when it mounts). The inner
                child is absolutely positioned underneath and fades in as the
                user scrolls past the hero. Video & expanded hero keep flowing
                naturally behind it. */}
            {isPublicDefault && (
              <div
                data-testid="compact-header-overlay"
                className="sticky top-0 z-20 w-full"
                style={{ height: 0, overflow: 'visible', pointerEvents: 'none' }}
                aria-hidden={scrollProgress < 0.5}
              >
                <div
                  className="w-full flex flex-col items-center px-5 pt-0.5 pb-0 transition-[opacity,transform] duration-300 ease-out"
                  style={{
                    background: 'rgba(240, 239, 237, 0.55)',
                    backdropFilter: 'blur(22px)',
                    WebkitBackdropFilter: 'blur(22px)',
                    opacity: hideChrome ? 0 : scrollProgress,
                    transform: `translateY(${hideChrome ? -16 : (-10 * (1 - scrollProgress))}px)`,
                    transition: 'opacity 260ms ease-out, transform 260ms ease-out',
                    pointerEvents: scrollProgress > 0.5 && !hideChrome ? 'auto' : 'none',
                    willChange: 'opacity, transform',
                  }}
                >
                  <button
                    data-testid="logo-btn-compact"
                    onClick={handleLogoClick}
                    className="flex flex-col items-center leading-none"
                  >
                    <div className="rounded-full overflow-hidden w-10 h-10 mb-0">
                      <img src={LOGO_URL} alt="Ruido.ai" className="w-full h-full object-cover scale-[1.15]" />
                    </div>
                    <h1 className="font-light tracking-tight text-gray-900 font-['Outfit'] text-center text-sm leading-none mt-0.5">
                      Ruido<span className="text-gray-400">.ai</span>
                    </h1>
                  </button>
                </div>
              </div>
            )}
            <div
              className={`${
                compactMode ? 'sticky top-0 z-10' : 'relative'
              } flex flex-col items-center px-5 transition-all duration-[300ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
                heroExpanded ? 'pt-10 sm:pt-14 pb-4' : 'pt-3 pb-2'
              } ${
                // Mobile-only hide-on-scroll-down. Desktop (md:) is forced visible.
                isPublicDefault && hideChrome
                  ? 'opacity-0 -translate-y-3 pointer-events-none md:opacity-100 md:translate-y-0 md:pointer-events-auto'
                  : 'opacity-100 translate-y-0'
              }`}
              style={compactMode ? { background: 'rgba(240, 239, 237, 0.4)', backdropFilter: 'blur(22px)', WebkitBackdropFilter: 'blur(22px)' } : undefined}
            >
          {/* Back arrow — only for logged-in users coming from account */}
          {cameFromAccount && !heroExpanded && (
            <button
              data-testid="back-to-account"
              onClick={() => navigate('/cliente')}
              className="absolute left-5 top-1/2 -translate-y-1/2 p-1.5 rounded-full text-gray-400 hover:text-gray-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          <button
            data-testid="logo-btn"
            onClick={handleLogoClick}
            className="flex flex-col items-center transition-all"
          >
            <div className={`rounded-full overflow-hidden transition-all duration-500 ${heroExpanded ? 'w-14 h-14 mb-6' : 'w-10 h-10 mb-0.5'}`}>
              <img src={LOGO_URL} alt="Ruido.ai" className="w-full h-full object-cover scale-[1.15]" />
            </div>
            <h1 className={`font-light tracking-tight text-gray-900 font-['Outfit'] text-center transition-all duration-500 ${heroExpanded ? 'text-2xl sm:text-3xl leading-[1.15] max-w-lg mx-auto' : 'text-sm'}`}>
              {heroExpanded ? (
                <>
                  High-end video content, created by real people
                  <span className="text-gray-400"> — powered by AI.</span>
                </>
              ) : (
                <>Ruido<span className="text-gray-400">.ai</span></>
              )}
            </h1>
          </button>

          {heroExpanded && (
            <div className="flex flex-col items-center animate-fade-in w-full max-w-xs">
              <p className="text-sm text-gray-400 text-center max-w-sm leading-relaxed mt-[18px]">
                Every video is crafted by a creative team: idea, script, design, animation and editing — accelerated with AI for speed, not automation.
              </p>

              {/* Inline auth form */}
              {!isLoggedIn && authMode ? (
                <div className="w-full mt-[30px] space-y-2.5">
                  {authError && <p className="text-xs text-red-500 text-center">{authError}</p>}
                  <input
                    data-testid="auth-email"
                    type="email"
                    value={authEmail}
                    onChange={e => setAuthEmail(e.target.value)}
                    placeholder="Email"
                    className="w-full px-4 py-2.5 rounded-xl bg-white/50 border border-gray-200/50 text-sm text-gray-900 outline-none focus:border-gray-300 placeholder:text-gray-400"
                  />
                  <div className="relative">
                    <input
                      data-testid="auth-password"
                      type={showPassword ? 'text' : 'password'}
                      value={authPassword}
                      onChange={e => setAuthPassword(e.target.value)}
                      placeholder="Password"
                      autoComplete={authMode === 'signup' ? 'new-password' : 'current-password'}
                      className="w-full px-4 py-2.5 pr-10 rounded-xl bg-white/50 border border-gray-200/50 text-sm text-gray-900 outline-none focus:border-gray-300 placeholder:text-gray-400"
                    />
                    <button
                      type="button"
                      data-testid="auth-password-toggle"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      title={showPassword ? 'Hide password' : 'Show password'}
                      className="absolute inset-y-0 right-2 my-auto h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 active:scale-95 transition-colors"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <button
                    data-testid="auth-submit"
                    onClick={async () => {
                      setAuthError('');
                      const fn = authMode === 'signup' ? signUp : signIn;
                      const { error, data } = await fn(authEmail, authPassword);
                      if (error) {
                        // If signup and account already exists, auto-switch to
                        // sign-in mode and pre-fill the email. Reduces friction
                        // for managers who try to re-register by mistake.
                        if (error.code === 'account_exists' && authMode === 'signup') {
                          setAuthMode('signin');
                          setAuthError('This account already exists. Sign in with your password.');
                          return;
                        }
                        setAuthError(error.message || 'Authentication failed');
                        if (error.code === 'email_not_confirmed') {
                          setNeedsConfirmation(true);
                        }
                        return;
                      }
                      // Link the anonymous sales conversation (if any) to this new user
                      const accessToken = data?.session?.access_token || data?.access_token;
                      if (accessToken) {
                        try { await linkSignup(accessToken); } catch { /* noop */ }
                      }
                      // Route by role: managers go straight to /gestor, everyone else /cliente.
                      const role = data?.profile?.role;
                      // Manager-login mode (entered via /manager command) requires
                      // a manager-role profile. Reject and sign back out otherwise.
                      if (managerLoginMode) {
                        if (role === 'manager' || role === 'admin') {
                          setManagerLoginMode(false);
                          navigate('/gestor');
                        } else {
                          await signOut();
                          setManagerLoginMode(false);
                          setShowAuth(false);
                          setAuthEmail('');
                          setAuthPassword('');
                          setAuthError('');
                          setChatMessages(prev => [
                            ...prev,
                            { role: 'assistant', text: 'Unauthorized access.' },
                          ]);
                        }
                        return;
                      }
                      if (role === 'manager' || role === 'admin') {
                        navigate('/gestor');
                      } else {
                        navigate('/cliente');
                      }
                    }}
                    className="w-full py-2.5 rounded-xl text-sm font-medium bg-gray-900 text-white hover:bg-gray-800 transition-all active:scale-[0.98]"
                  >
                    {authMode === 'signup' ? 'Create account' : 'Sign in'}
                  </button>
                  {needsConfirmation && (
                    <button
                      data-testid="auth-resend-confirmation"
                      onClick={async () => {
                        setAuthError('');
                        const { error, data } = await resendConfirmation(authEmail);
                        if (error) setAuthError(error.message);
                        else setAuthError(data?.message || 'Confirmation email resent.');
                      }}
                      className="w-full py-2 rounded-xl text-xs text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100 transition-colors"
                    >
                      Resend confirmation email
                    </button>
                  )}
                  <button
                    data-testid="auth-google"
                    onClick={signInWithGoogle}
                    className="w-full py-2.5 rounded-xl text-sm text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    Continue with Google
                  </button>
                  <button
                    onClick={() => { setAuthMode(authMode === 'signup' ? 'signin' : 'signup'); setAuthError(''); setNeedsConfirmation(false); }}
                    className="w-full text-center text-[11px] text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {authMode === 'signup' ? 'Already have an account? Sign in' : "Don't have an account? Create one"}
                  </button>
                </div>
              ) : !isLoggedIn ? (
                <div className="flex items-center gap-4 mt-[30px]">
                  <button
                    data-testid="auth-create"
                    onClick={() => setAuthMode('signup')}
                    className="text-xs font-medium text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    Create account
                  </button>
                  <span className="text-gray-300 text-[10px]">&middot;</span>
                  <button
                    data-testid="auth-signin"
                    onClick={() => setAuthMode('signin')}
                    className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    Sign in
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-4 mt-[30px]">
                  <button
                    data-testid="auth-account"
                    onClick={() => navigate('/cliente')}
                    className="text-xs font-medium text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    Account
                  </button>
                  <span className="text-gray-300 text-[10px]">&middot;</span>
                  <button
                    data-testid="auth-settings"
                    onClick={handleSettings}
                    className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    Settings
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
            </>
          );
        })()}

        {/* Phase 7 — Vertical sticky-stack showcase. Three 9:16 stories scroll
            past one at a time; only the most-visible one autoplays. Public-only,
            hidden when the inline auth pane is open so login takes focus. */}
        {isPublicDefault && !showAuth && (
          <ShowcaseStack scrollRoot={scrollAreaRef} />
        )}

        {/* Chat messages */}
        <div className="max-w-lg mx-auto w-full px-5 space-y-4 py-3 min-h-[50vh]">
          {chatMessages.map((msg, i) => (
            <div key={i} className={`flex gap-2.5 animate-slide-up ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
              {msg.role === 'assistant' && (
                <div className="w-5 h-5 rounded-full overflow-hidden shrink-0 mt-0.5">
                  <img src={LOGO_URL} alt="" className="w-full h-full object-cover scale-[1.15]" />
                </div>
              )}
                {msg.type === 'size-section' ? (
                  <div data-testid="size-section" className="max-w-[95%] w-full">
                    <div className="border border-gray-200/60 rounded-2xl bg-white/40 backdrop-blur-sm p-4">
                      <h3 className="text-sm font-medium text-gray-900 font-['Outfit'] leading-tight">{msg.title}</h3>
                      {msg.subtitle && (
                        <p className="text-[12px] text-gray-500 mt-1 mb-3 leading-snug whitespace-pre-line">{msg.subtitle}</p>
                      )}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {msg.cards.map((card) => (
                          <div
                            key={card.id}
                            data-testid={`size-card-${card.id}`}
                            className="border border-gray-200/60 rounded-xl px-3 py-2.5 bg-white/50 hover:border-gray-300 transition-colors"
                          >
                            <span className="text-xs font-medium text-gray-900 block">{card.name}</span>
                            <span className="text-[10px] text-gray-400 block">{card.duration}</span>
                            <div className="flex items-baseline gap-1.5 mt-1">
                              <span className="text-[11px] text-emerald-600 tabular-nums font-medium">
                                {card.credits} cr.
                              </span>
                              {card.approxPrice != null && (
                                <span className="text-[10px] text-gray-400 tabular-nums">
                                  ~${card.approxPrice}
                                </span>
                              )}
                            </div>
                            {card.description && (
                              <span className="text-[10px] text-gray-400 block mt-1.5 leading-tight">
                                {card.description}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                      <p className="text-[10px] text-gray-400 mt-2.5 leading-tight">
                        The approximate price is calculated with the most efficient package; the exact amount comes from whichever package you choose below.
                      </p>
                    </div>
                  </div>
                ) : msg.type === 'packages-section' ? (
                  <div data-testid="packages-section" className="max-w-[95%] w-full">
                    <div className="border border-gray-200/60 rounded-2xl bg-white/40 backdrop-blur-sm p-4">
                      <h3 className="text-sm font-medium text-gray-900 font-['Outfit'] leading-tight">{msg.title}</h3>
                      {msg.subtitle && (
                        <p className="text-[12px] text-gray-500 mt-1 mb-3 leading-snug">{msg.subtitle}</p>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        {msg.items.map((pkg) => (
                          <div
                            key={pkg.id}
                            data-testid={`package-${pkg.id}`}
                            className={`relative rounded-xl p-3 transition-all ${
                              pkg.featured
                                ? 'border-2 border-gray-900/15 bg-white/60 shadow-sm'
                                : 'border border-gray-200/60 bg-white/40'
                            }`}
                          >
                            {pkg.featured && (
                              <span className="absolute -top-1.5 right-3 text-[9px] uppercase tracking-[0.15em] bg-gray-900 text-white px-2 py-0.5 rounded-full">
                                Recommended
                              </span>
                            )}
                            {pkg.name && (
                              <span className="text-[10px] uppercase tracking-[0.15em] text-gray-400 block">
                                {pkg.name}
                              </span>
                            )}
                            <span className="text-sm font-medium text-gray-900 block">{pkg.qty}</span>
                            <div className="flex items-baseline gap-1 mt-0.5">
                              <span className="text-lg text-gray-900 font-['Outfit'] tabular-nums">
                                {pkg.price}
                              </span>
                              {pkg.unit && (
                                <span className="text-[10px] text-gray-400 tabular-nums">{pkg.unit}</span>
                              )}
                            </div>
                            {pkg.description && (
                              <p className="text-[10px] text-gray-400 mt-1 leading-tight line-clamp-2">{pkg.description}</p>
                            )}
                            <button
                              data-testid={`buy-${pkg.id}`}
                              onClick={() => handleGetCredits(pkg)}
                              className={`mt-2.5 w-full text-xs font-medium rounded-lg py-2 transition-colors ${
                                pkg.featured
                                  ? 'bg-gray-900 hover:bg-gray-800 text-white'
                                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                              }`}
                            >
                              {msg.stripeEnabled ? 'Buy credits' : 'Pre-order'}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : msg.type === 'sizes' ? (
                  <div className="flex flex-col gap-2 max-w-[95%]">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {msg.cards.map((card, j) => (
                        <div key={j} data-testid={`size-card-${card.id}`} className="border border-gray-200/60 rounded-xl px-3 py-2.5 bg-white/30">
                          <span className="text-xs font-medium text-gray-900 block">{card.name}</span>
                          <span className="text-[10px] text-gray-400 block">{card.duration}</span>
                          <span className="text-[10px] text-emerald-600 tabular-nums block mt-0.5">{card.credits} cr.</span>
                          {card.description && (
                            <span className="text-[10px] text-gray-400 block mt-1 leading-tight">{card.description}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : msg.type === 'plans' ? (
                  <div className="flex flex-col gap-2 max-w-[90%]">
                    <div className="flex gap-2 flex-wrap">
                      {msg.cards.map((card, j) => (
                        <div key={j} className="border border-gray-200/60 rounded-xl px-4 py-3 bg-white/30 min-w-[130px]">
                          <span className="text-sm font-medium text-gray-900 block mb-0.5">{card.name}</span>
                          <span className="text-[11px] text-gray-400 block mb-2">{card.duration}</span>
                          <span className="text-base font-light text-gray-900 font-['Outfit']">{card.price}</span>
                          <button
                            data-testid={`get-credits-${j}`}
                            onClick={() => handleGetCredits(card.name)}
                            className="mt-2 w-full py-1.5 rounded-lg text-[11px] font-medium bg-gray-900/[0.05] text-gray-600 hover:bg-gray-900/[0.08] hover:text-gray-900 transition-all"
                          >
                            Buy credits
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : msg.type === 'packages' ? (
                  <div className="flex gap-2.5 flex-wrap max-w-[95%]">
                    {msg.items.map((pkg, j) => (
                      <button
                        key={j}
                        data-testid={`package-${pkg.id || j}`}
                        onClick={() => handleGetCredits(pkg)}
                        className={`relative rounded-xl text-left transition-all duration-200 cursor-pointer hover:scale-[1.03] hover:shadow-md active:scale-[0.97] ${
                          pkg.featured
                            ? 'border-2 border-gray-900/10 bg-white/50 px-4 py-3.5 min-w-[170px] shadow-sm'
                            : 'border border-gray-200/50 bg-white/25 hover:bg-white/40 px-3.5 py-2.5'
                        }`}
                      >
                        {pkg.name && (
                          <span className={`text-gray-400 block ${pkg.featured ? 'text-[10px] uppercase tracking-[0.15em] mb-0.5' : 'text-[9px] uppercase tracking-[0.15em]'}`}>
                            {pkg.name}
                          </span>
                        )}
                        <span className={`font-medium text-gray-900 block ${pkg.featured ? 'text-sm mb-0.5' : 'text-xs'}`}>{pkg.qty}</span>
                        {pkg.price && (
                          <span className={`text-gray-700 font-['Outfit'] ${pkg.featured ? 'text-lg' : 'text-sm'}`}>{pkg.price}</span>
                        )}
                        {pkg.unit && (
                          <span className={`text-gray-400 ml-1 ${pkg.featured ? 'text-[11px]' : 'text-[10px]'}`}>{pkg.unit}</span>
                        )}
                        <span className={`block mt-2 text-center py-1.5 rounded-lg text-[11px] font-medium transition-colors ${
                          pkg.featured
                            ? 'bg-gray-900 text-white'
                            : 'bg-gray-900/[0.05] text-gray-600'
                        }`}>
                          {msg.stripeEnabled ? 'Comprar' : 'Pre-ordenar'}
                        </span>
                      </button>
                    ))}
                  </div>
                ) : msg.type === 'examples' ? (
                  <div className="flex gap-2.5 flex-wrap max-w-[90%]">
                    {msg.videos.map((vid, j) => (
                      <div key={j} className="border border-gray-200/60 rounded-xl overflow-hidden bg-white/30 w-[160px]">
                        <div className="relative aspect-video bg-gray-200/40">
                          <img src={vid.thumbnail} alt={vid.title} className="w-full h-full object-cover" />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-8 h-8 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center">
                              <Play className="w-3.5 h-3.5 text-gray-700 ml-0.5" />
                            </div>
                          </div>
                        </div>
                        <div className="px-3 py-2">
                          <span className="text-xs font-medium text-gray-900 block">{vid.title}</span>
                          <span className="text-[10px] text-gray-400">{vid.label}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : msg.type === 'actions' ? (
                  <div className="flex gap-1.5 flex-wrap max-w-[90%]">
                    {msg.actions.map((act, j) => (
                      <button
                        key={j}
                        data-testid={`chat-action-${act.action}`}
                        onClick={() => handleChatAction(act.action)}
                        className="px-3.5 py-1.5 bg-white/40 border border-gray-200/50 rounded-full text-xs text-gray-600 hover:text-gray-900 hover:bg-white/60 transition-all active:scale-[0.97]"
                      >
                        {act.label}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className={`text-sm leading-relaxed max-w-[85%] whitespace-pre-line ${
                    msg.role === 'user' ? 'text-gray-800' : 'text-gray-500'
                  }`}>
                    {msg.text}
                  </p>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
            {aiThinking && (
              <div data-testid="ai-thinking" className="flex gap-2.5 animate-slide-up">
                <div className="w-5 h-5 rounded-full overflow-hidden shrink-0 mt-0.5">
                  <img src={LOGO_URL} alt="" className="w-full h-full object-cover scale-[1.15]" />
                </div>
                <div className="flex items-center gap-1 px-1 py-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-pulse" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-pulse" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-pulse" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            )}
          </div>
          {/* Spacer so content can scroll fully above the floating input overlay.
              The overlay is absolutely positioned at bottom so users need this
              breathing room to reach the last chat message / wizard options. */}
          <div aria-hidden className="h-[160px]" />
        </div>

      {/* Phase 6 — Sales wizard + Floating input wrap.
          Both float over the scroll area via absolute positioning so there's
          NO reserved rectangle of the page background sitting behind the
          input — content (videos, chat) scrolls underneath. A spacer at the
          bottom of the scroll content ensures nothing gets permanently
          hidden behind the floating pill. */}
      <div className="absolute bottom-0 left-0 right-0 z-20 pointer-events-none">
        {isPublicDefault && !showAuth && (
          <div
            className={`pb-1 pointer-events-auto transition-[opacity,transform] duration-300 ease-out ${
              hideChrome
                ? 'opacity-0 translate-y-3 pointer-events-none md:opacity-100 md:translate-y-0 md:pointer-events-auto'
                : 'opacity-100 translate-y-0'
            }`}
          >
            <SalesWizard
              isLoggedIn={isLoggedIn}
              onAnswer={handleWizardAnswer}
              onComplete={handleWizardComplete}
              onSkip={handleWizardSkip}
            />
          </div>
        )}

        {/* Floating chips + input */}
        <div className="px-5 pb-[env(safe-area-inset-bottom,10px)] pt-0 pointer-events-none">
        <div className="max-w-lg mx-auto pointer-events-auto">
            {!isPublicDefault && (
            <div className="flex items-center justify-center gap-1.5 mb-1.5">
            <button
              data-testid="chip-examples"
              onClick={handleExamples}
              disabled={usedChips.examples}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all active:scale-[0.97] ${
                usedChips.examples
                  ? 'bg-gray-900/[0.03] text-gray-300 cursor-default'
                  : 'bg-white/40 border border-gray-200/50 text-gray-600 hover:text-gray-900 hover:bg-white/60'
              }`}
            >
              See examples
            </button>
            <button
              data-testid="chip-credits"
              onClick={handleCredits}
              disabled={usedChips.credits}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all active:scale-[0.97] ${
                usedChips.credits
                  ? 'bg-gray-900/[0.03] text-gray-300 cursor-default'
                  : 'bg-white/40 border border-gray-200/50 text-gray-600 hover:text-gray-900 hover:bg-white/60'
              }`}
            >
              Credits
            </button>
          </div>
            )}
          <div
            className={`flex items-center gap-1 rounded-full px-2 py-0.5 bg-white/80 backdrop-blur-md shadow-[0_2px_10px_rgba(0,0,0,0.04)] transition-all duration-200 ${
              voice.isActive ? 'ring-1 ring-emerald-400/40 shadow-[0_4px_24px_rgba(16,185,129,0.18)]' : ''
            }`}
            style={{ borderRadius: '999px' }}
          >
            {voice.isActive ? (
              <RecordingBar
                testIdPrefix="entry-voice"
                phase={voice.isUploading ? 'uploading' : 'recording'}
                levels={voice.levels}
                seconds={voice.seconds}
                maxSeconds={voice.maxSeconds}
                onConfirm={voice.confirm}
                onCancel={voice.cancel}
              />
            ) : (
              <>
                <button className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 transition-colors shrink-0">
                  <Plus className="w-[18px] h-[18px]" />
                </button>
                <textarea
                  data-testid="entry-chat-input"
                  ref={textareaRef}
                  value={chatInput}
                  onFocus={handleFocus}
                  onChange={(e) => {
                    setChatInput(e.target.value);
                    const el = e.target;
                    el.style.height = 'auto';
                    el.style.height = Math.min(el.scrollHeight, 140) + 'px';
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                  placeholder={dynamicPlaceholder}
                  className="flex-1 bg-transparent text-gray-900 text-[14px] resize-none outline-none px-1 py-1 leading-5 placeholder:text-gray-400 overflow-y-auto scrollbar-thin self-center"
                  rows={1}
                  style={{ maxHeight: '140px' }}
                />
                <button
                  type="button"
                  onClick={voice.start}
                  disabled={voice.isRequesting}
                  data-testid="entry-voice-mic-btn"
                  title="Hablar (transcribir con IA)"
                  aria-label="Hablar (transcribir con IA)"
                  className="p-1.5 rounded-full text-gray-400 hover:text-gray-700 transition-colors shrink-0 active:scale-[0.95] disabled:opacity-50"
                >
                  <Mic className="w-[16px] h-[16px]" />
                </button>
                <button
                  data-testid="entry-chat-send"
                  onClick={handleSend}
                  disabled={!canSend || aiThinking}
                  className={`p-1.5 rounded-full transition-all duration-200 shrink-0 ${(canSend && !aiThinking) ? 'text-emerald-500 hover:text-emerald-600' : 'text-gray-300 opacity-40 cursor-not-allowed'}`}
                >
                  <RocketIcon className="w-[34px] h-[34px]" />
                </button>
              </>
            )}
          </div>
          {voice.notice && (
            <div
              data-testid="entry-voice-notice"
              className="mt-1.5 text-center text-[11px] text-amber-600 animate-fade-in"
            >
              {voice.notice}
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}
